/**
 * Suno-Covert Frontend Application
 *
 * Secure, minimal frontend for the Suno Gateway API.
 */

import { ApiClient, ApiClientError } from "./services/api-client.js";
import type { StatusResponse, ClipInfo } from "./types/api.js";

// Configuration
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://ctf-suno-gateway.drip-6c1.workers.dev";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 120;

// State
interface Task {
  id: string;
  prompt: string;
  status: StatusResponse["status"];
  clips: ClipInfo[];
  pollCount: number;
  pollTimer?: number;
}

const state = {
  connected: false,
  tasks: new Map<string, Task>(),
};

// API Client
const api = new ApiClient(API_BASE_URL);

// LocalStorage keys
const STORAGE_KEYS = {
  tasks: "suno_tasks",
  apiKey: "suno_api_key",
};

// Persistence functions
function saveTasks(): void {
  const tasksArray = Array.from(state.tasks.values()).map((task) => ({
    ...task,
    pollTimer: undefined, // Don't persist timers
  }));
  try {
    localStorage.setItem(STORAGE_KEYS.tasks, JSON.stringify(tasksArray));
  } catch {
    // Storage full or unavailable
  }
}

function loadTasks(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.tasks);
    if (stored) {
      const tasksArray = JSON.parse(stored) as Task[];
      state.tasks.clear();
      tasksArray.forEach((task) => {
        state.tasks.set(task.id, { ...task, pollTimer: undefined });
      });
    }
  } catch {
    // Invalid data
  }
}

function saveApiKey(key: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.apiKey, key);
  } catch {
    // Storage unavailable
  }
}

function loadApiKey(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.apiKey);
  } catch {
    return null;
  }
}

function clearApiKey(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.apiKey);
  } catch {
    // Ignore
  }
}

// DOM Elements
const $ = <T extends HTMLElement>(id: string): T =>
  document.getElementById(id) as T;

const elements = {
  apiKeyForm: $<HTMLFormElement>("api-key-form"),
  apiKeyInput: $<HTMLInputElement>("api-key-input"),
  connectBtn: $<HTMLButtonElement>("connect-btn"),
  connectionDot: $<HTMLElement>("connection-dot"),
  connectionText: $<HTMLElement>("connection-text"),
  quotaDisplay: $<HTMLElement>("quota-display"),
  quotaValue: $<HTMLElement>("quota-value"),
  quotaProgress: $<HTMLElement>("quota-progress"),
  generateCard: $<HTMLElement>("generate-card"),
  generateForm: $<HTMLFormElement>("generate-form"),
  promptInput: $<HTMLTextAreaElement>("prompt-input"),
  styleInput: $<HTMLInputElement>("style-input"),
  generateBtn: $<HTMLButtonElement>("generate-btn"),
  tasksCard: $<HTMLElement>("tasks-card"),
  tasksContainer: $<HTMLElement>("tasks-container"),
  errorMessage: $<HTMLElement>("error-message"),
  errorText: $<HTMLElement>("error-text"),
};

// Utility functions
function showError(message: string): void {
  elements.errorText.textContent = message;
  elements.errorMessage.classList.remove("hidden");
  setTimeout(() => {
    elements.errorMessage.classList.add("hidden");
  }, 6000);
}

function clearError(): void {
  elements.errorMessage.classList.add("hidden");
}

function setConnected(connected: boolean): void {
  state.connected = connected;
  elements.connectionDot.classList.toggle("connected", connected);
  elements.connectionDot.classList.toggle("disconnected", !connected);
  elements.connectionText.textContent = connected ? "Online" : "Offline";
  elements.generateCard.classList.toggle("hidden", !connected);
  elements.tasksCard.classList.toggle("hidden", !connected);
  elements.quotaDisplay.classList.toggle("hidden", !connected);
  elements.connectBtn.textContent = connected ? "Disconnect" : "Connect";
  elements.apiKeyInput.disabled = connected;
}

function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getStatusClass(status: string): string {
  switch (status) {
    case "complete":
    case "streaming":
      return "status-complete";
    case "processing":
      return "status-processing";
    case "error":
      return "status-error";
    default:
      return "status-pending";
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function updateQuota(remaining: number, limit: number): void {
  elements.quotaValue.textContent = `${remaining.toLocaleString()} / ${limit.toLocaleString()}`;
  const percentage = limit > 0 ? (remaining / limit) * 100 : 0;
  elements.quotaProgress.style.width = `${percentage}%`;
}

// Render functions
function renderTasks(): void {
  if (state.tasks.size === 0) {
    elements.tasksContainer.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18V5l12-2v13"/>
            <circle cx="6" cy="18" r="3"/>
            <circle cx="18" cy="16" r="3"/>
          </svg>
        </div>
        <p class="empty-state-text">No tracks yet. Generate your first song above!</p>
      </div>
    `;
    return;
  }

  const html = `<div class="task-list">${Array.from(state.tasks.values())
    .map((task) => renderTask(task))
    .join("")}</div>`;

  elements.tasksContainer.innerHTML = html;

  // Attach download handlers
  state.tasks.forEach((task) => {
    if (task.status === "complete") {
      const downloadBtn = document.getElementById(`download-${task.id}`);
      downloadBtn?.addEventListener("click", () => downloadTask(task.id));
    }
  });
}

function renderTask(task: Task): string {
  const clips = task.clips
    .map(
      (clip) => `
    <div class="track">
      <div class="track-info">
        <div class="track-title">${escapeHtml(clip.title || "Generating...")}</div>
        <div class="track-meta">${clip.id.slice(0, 8)}...</div>
      </div>
      <span class="status ${getStatusClass(clip.status)}">${formatStatus(clip.status)}</span>
      ${
        clip.audio_url && (clip.status === "streaming" || clip.status === "complete")
          ? `<audio controls preload="none" src="${escapeHtml(clip.audio_url)}"></audio>`
          : ""
      }
    </div>
  `
    )
    .join("");

  const isProcessing = task.status === "processing" || task.status === "pending";

  return `
    <div class="task-card">
      <div class="task-header">
        <div class="task-info">
          <div class="task-prompt">${escapeHtml(task.prompt)}</div>
          <div class="task-id">${task.id.slice(0, 8)}...</div>
        </div>
        <span class="status ${getStatusClass(task.status)}">${formatStatus(task.status)}</span>
      </div>
      ${clips ? `<div class="track-list">${clips}</div>` : ""}
      ${
        isProcessing
          ? `
        <div class="loading-text" style="margin-top: var(--space-4);">
          <div class="spinner"></div>
          <span>Generating your music...</span>
        </div>
      `
          : ""
      }
      ${
        task.status === "complete"
          ? `
        <button class="btn btn-secondary" id="download-${task.id}" style="margin-top: var(--space-4);">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Download MP3
        </button>
      `
          : ""
      }
    </div>
  `;
}

// API functions
async function connect(): Promise<void> {
  const apiKey = elements.apiKeyInput.value.trim();
  if (!apiKey) {
    showError("Please enter an API key");
    return;
  }

  elements.connectBtn.disabled = true;
  elements.connectBtn.innerHTML = '<div class="spinner spinner-sm"></div>';
  clearError();

  try {
    api.setApiKey(apiKey);
    const quota = await api.quota();

    // Save API key and load tasks
    saveApiKey(apiKey);
    loadTasks();
    renderTasks();

    // Resume polling for incomplete tasks
    state.tasks.forEach((task) => {
      if (task.status === "processing" || task.status === "pending") {
        pollTask(task.id);
      }
    });

    updateQuota(quota.credits_remaining, quota.monthly_limit);
    setConnected(true);
  } catch (err) {
    api.clearApiKey();
    if (err instanceof ApiClientError) {
      if (err.code === "SESSION_EXPIRED") {
        showError("Session expired. Please contact admin to refresh.");
      } else {
        showError(`Connection failed: ${err.message}`);
      }
    } else {
      showError("Connection failed. Check your API key.");
    }
  } finally {
    elements.connectBtn.disabled = false;
    elements.connectBtn.textContent = state.connected ? "Disconnect" : "Connect";
  }
}

function disconnect(): void {
  api.clearApiKey();
  clearApiKey();
  setConnected(false);
  state.tasks.forEach((task) => {
    if (task.pollTimer) {
      clearTimeout(task.pollTimer);
    }
  });
  state.tasks.clear();
  saveTasks();
  renderTasks();
  elements.apiKeyInput.value = "";
}

async function generate(): Promise<void> {
  const prompt = elements.promptInput.value.trim();
  if (!prompt) {
    showError("Please enter a prompt");
    return;
  }

  elements.generateBtn.disabled = true;
  elements.generateBtn.innerHTML = '<div class="spinner spinner-sm"></div> Generating...';
  clearError();

  try {
    const style = elements.styleInput.value.trim() || undefined;
    const response = await api.generate({ prompt, style });

    const task: Task = {
      id: response.task_id,
      prompt,
      status: "processing",
      clips: response.clips,
      pollCount: 0,
    };

    state.tasks.set(task.id, task);
    saveTasks();
    renderTasks();

    // Start polling
    pollTask(task.id);

    // Clear form
    elements.promptInput.value = "";
    elements.styleInput.value = "";

    // Refresh quota
    refreshQuota();
  } catch (err) {
    if (err instanceof ApiClientError) {
      if (err.code === "SESSION_EXPIRED") {
        showError("Session expired. Please contact admin to refresh.");
      } else {
        showError(`Generation failed: ${err.message}`);
      }
    } else {
      showError("Generation failed. Please try again.");
    }
  } finally {
    elements.generateBtn.disabled = false;
    elements.generateBtn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"/>
      </svg>
      Generate
    `;
  }
}

async function pollTask(taskId: string): Promise<void> {
  const task = state.tasks.get(taskId);
  if (!task || task.status === "complete" || task.status === "error") {
    return;
  }

  if (task.pollCount >= MAX_POLL_ATTEMPTS) {
    task.status = "error";
    saveTasks();
    renderTasks();
    return;
  }

  try {
    const status = await api.status(taskId);
    task.status = status.status;
    task.clips = status.clips || task.clips;
    task.pollCount++;
    saveTasks();
    renderTasks();

    if (status.status === "processing" || status.status === "pending") {
      task.pollTimer = window.setTimeout(
        () => pollTask(taskId),
        POLL_INTERVAL_MS
      );
    }
  } catch (err) {
    console.error("Poll error:", err);
    task.pollTimer = window.setTimeout(
      () => pollTask(taskId),
      POLL_INTERVAL_MS
    );
  }
}

async function downloadTask(taskId: string): Promise<void> {
  const btn = document.getElementById(`download-${taskId}`) as HTMLButtonElement;
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<div class="spinner spinner-sm"></div> Downloading...';
  }

  try {
    const blob = await api.download(taskId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suno-${taskId.slice(0, 8)}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Download failed: ${err.message}`);
    } else {
      showError("Download failed. Please try again.");
    }
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        Download MP3
      `;
    }
  }
}

async function refreshQuota(): Promise<void> {
  try {
    const quota = await api.quota();
    updateQuota(quota.credits_remaining, quota.monthly_limit);
  } catch {
    // Silently fail quota refresh
  }
}

// Event handlers
elements.apiKeyForm.addEventListener("submit", (e) => {
  e.preventDefault();
  if (state.connected) {
    disconnect();
  } else {
    connect();
  }
});

elements.generateForm.addEventListener("submit", (e) => {
  e.preventDefault();
  generate();
});

// Keyboard shortcuts
document.addEventListener("keydown", (e) => {
  // Cmd/Ctrl + Enter to submit generate form
  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
    if (document.activeElement === elements.promptInput && state.connected) {
      e.preventDefault();
      generate();
    }
  }
});

// Initialize
setConnected(false);

// Restore saved API key and auto-connect
const savedApiKey = loadApiKey();
if (savedApiKey) {
  elements.apiKeyInput.value = savedApiKey;
  // Auto-connect with saved key
  connect();
}
